<template>
  <SearchableSelectComponent
    :options="combinedOptions"
    :modelValue="currentOption?.value"
    :placeholder="t('go_send.recipient_search_placeholder')"
    :isLoading="isLoading"
    :isError="hasError"
    autoOpen
    @update:modelValue="handleOptionSelected"
    @queryUpdated="handleQueryUpdated"
  >
    <template v-if="isHostedSelected" #trailing>
      <span class="input-group-text">
        <span class="badge bg-primary">{{
          t("hosted_accounts.hosted_account")
        }}</span>
      </span>
    </template>
    <template #description="{ option }">
      <div
        v-if="isHostedOption(option)"
        class="text-muted small text-truncate d-flex align-items-center gap-2"
      >
        <span class="badge bg-primary">{{
          t("hosted_accounts.hosted_account")
        }}</span>
        <span>{{ t("go_send.recipient_hosted_helper") }}</span>
      </div>
      <div
        v-else-if="option.description"
        class="text-muted small text-truncate"
      >
        {{ option.description }}
      </div>
    </template>
  </SearchableSelectComponent>
</template>

<script setup lang="ts">
/**
 * Self-contained recipient picker for the Send GO flow.
 *
 * - Hits TWO endpoints in parallel above the min-query threshold:
 *   * `/api/send-recipients` (hosted-account-enabled companies)
 *   * `/api/utils/companies/` (global producer catalog for EXTERNAL sends)
 * - Hosted rows are rendered first and tagged `[HOSTED]`. External rows
 *   render with country + EECS, matching the legacy dropdown.
 * - Row keys are prefixed (`hosted-` / `external-`) to avoid integer-id
 *   collisions between the two sources.
 * - The model is the whole selection, written once per pick or keystroke. A model naming a hosted
 *   company renders as that hosted option even before any search has run (a saved PPA reopened).
 *   Typing over a picked company drops it, along with the account it proposed.
 * - All picker logic + types live here by design; no separate composable.
 */
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { RecipientSelection } from "@/dashboard/inventory_management/send/sendRecipient";
import { debounce } from "lodash-es";
import { computed, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;
const HOSTED_KEY_PREFIX = "hosted-";
const CUSTOM_ENTRY_KEY = "custom_entry";

interface HostedRecipient {
  company_id: number;
  name: string;
  country?: string | null;
}

interface GlobalCompany {
  id: number;
  name: string;
  business_id?: string;
  vat?: string;
  address?: string;
  country: string;
  account_eecs?: string;
}

interface RecipientOption {
  label: string;
  value: string;
  description?: string;
  selection: RecipientSelection;
}

const props = defineProps<{
  /** Company name read from an uploaded document, offered as an "AI extracted" entry. */
  displayValue?: string;
}>();

const model = defineModel<RecipientSelection>({ required: true });

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

const hostedRecipients = ref<HostedRecipient[]>([]);
const externalCompanies = ref<GlobalCompany[]>([]);
const isLoading = ref(false);
const hasError = ref(false);
const pickedExternal = ref<RecipientOption | undefined>();

const toHostedOption = (recipient: HostedRecipient): RecipientOption => ({
  label: recipient.name,
  value: `${HOSTED_KEY_PREFIX}${recipient.company_id}`,
  selection: {
    companyId: recipient.company_id,
    companyName: recipient.name,
    eecsAccountNumber: "",
  },
});

const isHostedOption = (option: { value?: string }): boolean =>
  option.value?.startsWith(HOSTED_KEY_PREFIX) ?? false;

const hostedOptions = computed(() =>
  hostedRecipients.value.map(toHostedOption)
);

const externalOptions = computed(() =>
  externalCompanies.value.map((company): RecipientOption => ({
    label: company.name,
    value: `external-${company.id}`,
    description: [
      company.country,
      company.business_id ? `ID: ${company.business_id}` : null,
      company.account_eecs ? `EECS: ${company.account_eecs}` : null,
    ]
      .filter(Boolean)
      .join(" | "),
    selection: {
      companyId: null,
      companyName: company.name,
      eecsAccountNumber: company.account_eecs ?? "",
    },
  }))
);

// Held apart from the search results, which a later search may replace without it. Dropped once the
// model moves off it, e.g. when the parent resets the form after a send.
const pickedExternalOption = computed(() =>
  pickedExternal.value?.label === model.value.companyName
    ? pickedExternal.value
    : undefined
);

const listedOptions = computed(() => [
  ...hostedOptions.value,
  ...externalOptions.value,
]);

/**
 * The option standing for the model: the hosted company it names, the external row last picked,
 * or else its typed / AI-extracted name as a free-text entry unless a listed row already carries
 * that name.
 */
const currentOption = computed<RecipientOption | null>(() => {
  const { companyId, companyName } = model.value;
  if (companyId != null) {
    return toHostedOption({ company_id: companyId, name: companyName });
  }
  if (pickedExternalOption.value) {
    return pickedExternalOption.value;
  }
  const text = props.displayValue ?? companyName;
  if (!text || listedOptions.value.some((option) => option.label === text)) {
    return null;
  }
  return {
    label: text,
    value: CUSTOM_ENTRY_KEY,
    description: props.displayValue ? "AI extracted" : "Custom entry",
    selection: { companyId: null, companyName: text, eecsAccountNumber: "" },
  };
});

const combinedOptions = computed(() => {
  const current = currentOption.value;
  const isListed =
    current !== null &&
    listedOptions.value.some((option) => option.value === current.value);
  return current && !isListed
    ? [current, ...listedOptions.value]
    : listedOptions.value;
});

const isHostedSelected = computed(() => model.value.companyId != null);

const fetchRecipients = async (query: string): Promise<void> => {
  if (query.length < MIN_QUERY_LENGTH) {
    hostedRecipients.value = [];
    externalCompanies.value = [];
    return;
  }
  isLoading.value = true;
  hasError.value = false;
  try {
    const [hostedResponse, externalResponse] = await Promise.all([
      goSolidApi.get("/api/send-recipients", { params: { search: query } }),
      goSolidApi.get("/api/utils/companies/", { params: { search: query } }),
    ]);
    hostedRecipients.value = hostedResponse.data?.recipients ?? [];
    externalCompanies.value = externalResponse.data ?? [];
  } catch (error) {
    globalErrorHandler(error);
    hasError.value = true;
    hostedRecipients.value = [];
    externalCompanies.value = [];
  } finally {
    isLoading.value = false;
  }
};

const debouncedFetchRecipients = debounce(fetchRecipients, SEARCH_DEBOUNCE_MS);

onBeforeUnmount(() => {
  debouncedFetchRecipients.cancel();
});

const handleQueryUpdated = (query: string): void => {
  const hadPick =
    model.value.companyId != null || pickedExternalOption.value !== undefined;
  pickedExternal.value = undefined;
  model.value = hadPick
    ? { companyId: null, companyName: query, eecsAccountNumber: "" }
    : { ...model.value, companyName: query };
  debouncedFetchRecipients(query);
};

const handleOptionSelected = (key: string | undefined): void => {
  const option = combinedOptions.value.find((o) => o.value === key);
  if (!option) {
    return;
  }
  pickedExternal.value =
    option.selection.companyId == null && key !== CUSTOM_ENTRY_KEY
      ? option
      : undefined;
  model.value = option.selection;
};
</script>

<style scoped lang="scss"></style>
